import Animated, { useAnimatedStyle } from "@-/com.npmjs.react-native-reanimated";
import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import {
  usePrimaryNavigationCustomizationSnapshot,
  type PrimaryNavigationCustomizationController,
} from "#primary-navigation-customization-controller";
import type { PrimaryNavigationCustomizationWorklets } from "#primary-navigation-customization-drag";
import { PRIMARY_NAVIGATION_DESTINATION_CHIP_SIZE } from "#primary-navigation-customization-constants";
import { PrimaryNavigationDestinationChip } from "#primary-navigation-destination-chip";

const HALF_CHIP = PRIMARY_NAVIGATION_DESTINATION_CHIP_SIZE / 2;

/**
 * One ghost per offered destination, each shown only while its destination is dragged. Keeping
 * them all mounted means a pickup never waits for a JS render to paint the right icon.
 */
function PrimaryNavigationCustomizationGhost({
  destination,
  worklets,
}: {
  destination: PrimaryNavigationDestination;
  worklets: PrimaryNavigationCustomizationWorklets;
}) {
  const style = useAnimatedStyle(() => {
    const isDragged = worklets.drag.get()?.destination === destination;
    return {
      opacity: isDragged ? worklets.ghostOpacity.get() : 0,
      transform: [
        { translateX: worklets.ghostX.get() - HALF_CHIP },
        { translateY: worklets.ghostY.get() - HALF_CHIP },
        { scale: worklets.ghostScale.get() },
      ],
    };
  });
  return (
    <Animated.View className="absolute top-0 left-0" style={style}>
      <PrimaryNavigationDestinationChip destination={destination} variant="lifted" />
    </Animated.View>
  );
}

/**
 * The layer the dragged icon travels on: above the scenes and the strip alike, so an icon can
 * leave the Settings page's scroll view and land in the strip (and back).
 */
export function PrimaryNavigationCustomizationGhostLayer({
  controller,
}: {
  controller: PrimaryNavigationCustomizationController;
}) {
  const snapshot = usePrimaryNavigationCustomizationSnapshot(controller);
  return (
    <Animated.View
      className="absolute inset-0"
      collapsable={false}
      pointerEvents="none"
      ref={controller.worklets.ghostLayerRef}
    >
      {snapshot?.available?.map((destination) => (
        <PrimaryNavigationCustomizationGhost
          destination={destination}
          key={destination}
          worklets={controller.worklets}
        />
      ))}
    </Animated.View>
  );
}
